import { CardDescription, CardHeader } from '@/components/ui/card';
import PaddockAvatar from '@/components/ui/paddock-avatar/paddock-avatar';
import { toast } from '@/hooks/use-toast';
import { useUser } from '@/hooks/use-user';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import { replyStore } from '@/models/stores';
import type { Reply, Trade, User } from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { isEmpty, trim } from 'lodash';
import { ArrowUpCircle, PaperclipIcon } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { getUserMentionIds } from '../../../deal/activity-feed/tip-tap-editor/get-user-mentions-ids';
import PaddockBubbleMenu from '../../../deal/activity-feed/tip-tap-editor/paddock-bubble-menu';
import PdkEditorContent from '../../../deal/activity-feed/tip-tap-editor/pdk-editor-content';
import { usePaddockEditor } from '../../../deal/activity-feed/tip-tap-editor/use-paddock-editor';

type ReplyPayload = {
  id: string;
  content: string; // stringified jsonb
  text: string;
  mentions: User['id'][];
};

export const OPTIMISTIC_REPLY_ID_PREFIX = 'optimistic-reply-';

const AddReplyTipTap = ({
  commentId,
  tradeId,
}: {
  commentId: string;
  tradeId: Trade['id'];
}) => {
  const { editor } = usePaddockEditor({
    placeholder: 'Leave a reply...',
  });

  const user = useUser();

  const addReply = useMutation({
    onMutate: async ({ reply }) => {
      const tradeReply: Reply = {
        id: reply.id,
        comment_id: commentId,
        created_at: dayjs().toISOString(),
        updated_at: dayjs().toISOString(),
        edited: false,
        text: reply.text,
        mentions: reply.mentions,
        user_id: user.id,
        content: JSON.parse(reply.content),
      };

      replyStore.add(tradeReply);
    },
    mutationFn: async ({ reply }: { reply: ReplyPayload }) => {
      if (!reply) return;

      // /trades/:trade_id/comments/:comment_id/reply
      const response = await pdkAxios
        .post(`/trades/${tradeId}/comments/${commentId}/reply`, {
          id: reply.id,
          content: reply.content,
          text: reply.text,
          mentions: reply.mentions,
        })
        .catch((e) => {
          console.error('Error adding reply', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {
      editor?.commands.setContent({});
    },
    onError: (error, variables) => {
      // UX Note: restore the content of the editor

      editor?.commands.setContent(JSON.parse(variables?.reply?.content));
      console.error('Error adding reply', error, variables);

      toast({
        title: 'Error adding reply',
        description: error.message,
      });
    },
    onSettled: () => {
      // DEV NOTE: invalidateQueries is cause a flicker bug
      // because the useQuery hook in useDeal re runs and gives back an old value
      // It may not even be refetching ...
      // queryClient.invalidateQueries({ queryKey: ["deal", id] });
    },
  });

  const {
    // id: userId,
    first_name,
    last_name,
    //  email, role,
    avatar,
  } = useUser();
  const handleSubmitReply = () => {
    const content = editor?.getJSON();

    const text = editor?.getText() || '';

    if (isEmpty(trim(text))) {
      return;
    }

    const mentionIds = getUserMentionIds(editor);

    editor?.commands.setContent({});
    addReply.mutate({
      reply: {
        id: uuidv4(),
        content: JSON.stringify(content),
        text,
        mentions: mentionIds,
      },
    });
  };

  return (
    <CardHeader className="p-0">
      <CardDescription className="flex flex-row gap-2 items-start pt-2">
        <PaddockAvatar
          src={avatar ?? ''}
          alt={`${first_name} ${last_name}`}
          fallback={`${first_name.charAt(0)} ${last_name.charAt(0)}`}
          className="size-4 mt-0.5"
        />
        <div className="w-full">
          <PaddockBubbleMenu editor={editor} />
          <PdkEditorContent
            editor={editor}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.metaKey) {
                e.preventDefault();
                handleSubmitReply();
              }

              if (e.key === HOTKEYS.GLOBAL_UNFOCUS_OR_CLOSE) {
                editor?.commands.blur();
              }
            }}
          />
        </div>
        <div className="flex flex-row w-fit justify-end gap-2 h-10 items-center">
          <PaperclipIcon
            className={cn(
              'h-5 w-5 cursor-pointer text-gray-500 hover:fill-gray-100',
              {},
            )}
            onClick={() => {
              toast({
                title: 'Feature not implemented yet',
                description:
                  "Slack us if you want the 'reply attachments' feature",
              });
            }}
          />
          <ArrowUpCircle
            className={cn(
              'h-5 w-5 cursor-pointer text-gray-500 hover:fill-gray-100',
            )}
            onClick={handleSubmitReply}
          />
        </div>
      </CardDescription>
    </CardHeader>
  );
};

export default AddReplyTipTap;
